import React, { useState } from 'react';
import toast from 'react-hot-toast';

const SimpleTest: React.FC = () => {
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const addResult = (message: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testBasicConnection = async () => {
    setLoading(true);
    addResult('Testing basic connection to server...');
    
    try {
      const response = await fetch('http://localhost:3001/health');
      const data = await response.json();
      addResult(`✅ Server health: ${JSON.stringify(data)}`);
    } catch (error: any) {
      addResult(`❌ Server connection failed: ${error.message}`);
    }
    
    setLoading(false);
  };

  const testAuth = async () => {
    setLoading(true);
    addResult('Testing authentication...');
    
    try {
      const token = localStorage.getItem('token');
      const siteId = localStorage.getItem('activeSiteId');
      
      addResult(`Token exists: ${!!token}`);
      addResult(`Site ID exists: ${!!siteId}`);
      addResult(`Token: ${token?.substring(0, 20)}...`);
      addResult(`Site ID: ${siteId}`);
      
      if (!token) {
        addResult('❌ No token found - please login first');
        setLoading(false);
        return;
      }
      
      const response = await fetch('http://localhost:3001/api/pos/services', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-site-id': siteId || '',
          'Content-Type': 'application/json'
        }
      });
      
      addResult(`Services API status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addResult(`✅ Auth working - found ${Array.isArray(data) ? data.length : 0} services`);
      } else {
        const error = await response.json();
        addResult(`❌ Auth failed: ${JSON.stringify(error)}`);
      }
    } catch (error: any) {
      addResult(`❌ Auth test failed: ${error.message}`);
    }
    
    setLoading(false);
  };

  const testEODDirect = async () => {
    setLoading(true);
    addResult('Testing EOD API directly...');
    
    try {
      const token = localStorage.getItem('token');
      const siteId = localStorage.getItem('activeSiteId');
      const today = new Date().toISOString().split('T')[0];
      
      if (!token) {
        addResult('❌ No token found - please login first');
        setLoading(false);
        return;
      }
      
      addResult(`Making request to: http://localhost:3001/api/pos/reports/end-of-day?date=${today}`);
      addResult(`Headers: Authorization=Bearer ${token?.substring(0, 20)}..., x-site-id=${siteId}`);
      
      const response = await fetch(`http://localhost:3001/api/pos/reports/end-of-day?date=${today}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-site-id': siteId || '',
          'Content-Type': 'application/json'
        }
      });
      
      addResult(`EOD API status: ${response.status}`);
      addResult(`EOD API status text: ${response.statusText}`);
      
      const data = await response.json();
      addResult(`EOD Response: ${JSON.stringify(data, null, 2)}`);
      
      if (response.ok) {
        addResult(`✅ EOD API working!`);
      } else {
        addResult(`❌ EOD API failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      addResult(`❌ EOD test failed: ${error.message}`);
    }
    
    setLoading(false);
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Simple Connection Test</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Connection</h2>
          <div className="flex flex-wrap gap-4 mb-4">
            <button
              onClick={testBasicConnection}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Test Server
            </button>
            <button
              onClick={testAuth}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              Test Auth
            </button>
            <button
              onClick={testEODDirect}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              Test EOD Direct
            </button>
            <button
              onClick={clearResults}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Clear Results
            </button>
          </div>
          
          <div className="text-sm text-gray-600 mb-4">
            <p>Current URL: {window.location.href}</p>
            <p>Backend URL: http://localhost:3001</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h2>
          <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
            {results.length === 0 ? (
              <p className="text-gray-500">No tests run yet. Click a test button above.</p>
            ) : (
              <div className="space-y-2">
                {results.map((result, index) => (
                  <div key={index} className="text-sm font-mono whitespace-pre-wrap">
                    {result}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleTest;
